// Manual "refresh from your banks" — shared, app-wide sync state.
//
// Runs a LIVE incremental Plaid /transactions/sync (the product we pay for) for
// ALL the user's items, streamed as Server-Sent Events so we can narrate
// progress (requested → pulling each bank → updated · N new). The pull is inline
// on the server, so by the time the stream ends the new rows are in the DB and a
// single invalidate reflects them. The last-sync timestamp is persisted so the
// tooltip survives reloads.
//
// State is module-level: the trigger lives in the sidebar's account pill, while
// the mobile top bar renders the progress pip from the same values.

import { computed, ref } from 'vue'
import type { QueryClient } from '@tanstack/vue-query'
import { authenticatedFetch } from '@/utils/api'
import { invalidateFinancialData } from '@/utils/cacheUtils'

const LAST_SYNC_KEY = 'lp-last-sync'

const isSyncing = ref(false)
const syncMessage = ref('')
const syncTone = ref<'info' | 'success' | 'error'>('info')
const lastSynced = ref<string | null>(
  typeof localStorage !== 'undefined' ? localStorage.getItem(LAST_SYNC_KEY) : null,
)
let syncResetTimer: number | undefined

function relativeTime(iso: string | null): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return ''
  const s = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (s < 45) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

type SyncEvent = {
  phase: 'start' | 'item_start' | 'item_done' | 'item_error' | 'done'
  total?: number
  index?: number
  institution?: string
  added?: number
  failed_items?: number
  last_synced?: string | null
}

function applySyncEvent(evt: SyncEvent) {
  switch (evt.phase) {
    case 'start':
      syncMessage.value =
        (evt.total ?? 0) > 1 ? `Syncing ${evt.total} accounts…` : 'Syncing…'
      break
    case 'item_start':
      syncMessage.value =
        (evt.total ?? 1) > 1
          ? `Pulling ${evt.institution}… (${evt.index}/${evt.total})`
          : `Pulling ${evt.institution}…`
      break
    case 'item_done':
      if (evt.added) {
        syncMessage.value = `${evt.institution}: +${evt.added} new`
      }
      break
    case 'done': {
      const added = evt.added ?? 0
      const failed = evt.failed_items ?? 0
      if (failed > 0) {
        // Some banks pulled fine, others (e.g. ones needing re-auth) didn't —
        // say so rather than implying a clean sync.
        syncTone.value = 'error'
        const gotNew = added > 0 ? `${added} new · ` : ''
        syncMessage.value = `${gotNew}${failed} account${failed === 1 ? '' : 's'} failed`
      } else {
        syncTone.value = added > 0 ? 'success' : 'info'
        syncMessage.value =
          added > 0
            ? `Updated · ${added} new transaction${added === 1 ? '' : 's'}`
            : 'Up to date'
      }
      if (evt.last_synced) {
        lastSynced.value = evt.last_synced
        try { localStorage.setItem(LAST_SYNC_KEY, evt.last_synced) } catch { /* ignore */ }
      }
      break
    }
  }
}

// Read an SSE body, dispatching each `data: {...}` frame to applySyncEvent.
async function consumeSyncStream(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    let sep: number
    while ((sep = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, sep)
      buf = buf.slice(sep + 2)
      const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
      if (!dataLine) continue
      try {
        applySyncEvent(JSON.parse(dataLine.slice(5).trim()))
      } catch { /* ignore malformed frame */ }
    }
  }
}

interface AuthLike {
  isAuthenticated?: { value?: boolean }
  getApiToken?: unknown
  getAccessTokenSilently?: unknown
}

export function useTransactionSync() {
  const syncTitle = computed(() => {
    if (isSyncing.value) return syncMessage.value || 'Refreshing…'
    const rel = relativeTime(lastSynced.value)
    return rel ? `Refresh · last synced ${rel}` : 'Refresh data from your banks'
  })

  async function refresh(auth: AuthLike, queryClient: QueryClient) {
    if (isSyncing.value || !auth?.isAuthenticated?.value) return
    if (syncResetTimer) { window.clearTimeout(syncResetTimer); syncResetTimer = undefined }
    isSyncing.value = true
    syncTone.value = 'info'
    syncMessage.value = 'Requesting latest…'
    const started = Date.now()
    try {
      const resp = await authenticatedFetch(
        '/api/sync_transactions/stream',
        { method: 'POST', body: {}, headers: { Accept: 'text/event-stream' } },
        {
          getApiToken: (auth as any).getApiToken,
          getAccessTokenSilently: (auth as any).getAccessTokenSilently,
          isAuthenticated: auth.isAuthenticated,
        } as any,
      )
      if (!resp.ok || !resp.body) throw new Error(`sync failed (${resp.status})`)
      await consumeSyncStream(resp.body)
      // New rows are already committed server-side — reflect them in one pass.
      invalidateFinancialData(queryClient)
      // A sync is the moment sealed rows can fall behind: the server can't
      // write Plaid's changes into one, so it flags them instead. Tell whoever
      // is listening (useSealedMerge) that now is the time to catch up.
      window.dispatchEvent(new CustomEvent('app:transactions-synced'))
    } catch (e) {
      console.error('[useTransactionSync] refresh failed:', e)
      syncTone.value = 'error'
      syncMessage.value = 'Sync failed — try again'
    } finally {
      // Hold the final message briefly so a fast sync still reads as an action,
      // then clear the pip.
      const elapsed = Date.now() - started
      syncResetTimer = window.setTimeout(() => {
        isSyncing.value = false
        syncMessage.value = ''
        syncTone.value = 'info'
      }, Math.max(1400, 2200 - elapsed))
    }
  }

  return { isSyncing, syncMessage, syncTone, lastSynced, syncTitle, refresh }
}
